import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { FetchStatus } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import {
  COMMON_MIXPANEL_PROPERTIES,
  MIXPANEL_VENDOR_API_URL,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { shouldDisableCostlyVendors } from '@lyrahealth-inc/shared-app-logic/src/features/flags/utils'
import { User } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'

import {
  getConnectedServiceLoadingStatus,
  getIsPersonifyActivated,
} from '../../data/connectedService/connectedServiceSelectors'
import { getBenefitPartner } from '../../data/customer/customerSelectors'
import mixpanel from '../../data/mixpanel/mixpanel-browser'
import { getSessionId } from '../../data/session/sessionSelectors'
import { APP_ID, APP_PLATFORM, mixpanelReplaysPercent } from '../constants/appConstants'

function mixpanelTrackingOptInOrOut(username: string | undefined) {
  /**
   * Remove tracking opt in/out flag to default user to being tracked
   * Opt in flag set to true => user is tracked
   * Opt in flag set to false => user is not tracked
   * No opt in flag => user is tracked
   */
  mixpanel.clear_opt_in_out_tracking()

  // Check for a lyrahealth username. If in the production environment,
  // we do not want to log their events in Mixpanel
  const lyraEmployeeEmailsRegex = /@(lyrahealth|lyraclinical)\.com$/
  username = username ?? ''
  const isLyraEmployee = lyraEmployeeEmailsRegex.test(username)
  if ((process.env.NODE_ENV === 'production' && isLyraEmployee) || shouldDisableCostlyVendors) {
    mixpanel.opt_out_tracking()
  } else {
    mixpanel.opt_in_tracking()
  }
}

const useMixpanel = ({ mixpanelToken, user }: { mixpanelToken: string; user: User }) => {
  const [mixpanelInitialized, setMixpanelInitialized] = useState(false)
  const { enableMixpanelDebug } = useFlags()
  const cookieDomain = window.location.hostname
  const sessionId = useSelector(getSessionId)
  const connectedServiceLoadingStatus = useSelector(getConnectedServiceLoadingStatus)
  const isPersonifyActivated = useSelector(getIsPersonifyActivated)
  const benefitPartner = useSelector(getBenefitPartner)

  useEffect(() => {
    if (mixpanelToken && !mixpanelInitialized) {
      mixpanel.init(mixpanelToken, {
        ignore_dnt: true,
        api_host: MIXPANEL_VENDOR_API_URL,
        ...(enableMixpanelDebug && { debug: true }),
        loaded: () => {
          mixpanel.register({
            app_platform: APP_PLATFORM,
            app_id: APP_ID,
          })
          mixpanel.unregister('essentials')
          setMixpanelInitialized(true)
        },
        cookie_domain: cookieDomain, // ensure we are placing the full sub-domain (and not the parent domain) as the cookie domain
        secure_cookie: true, // prevent transmitting cookie with non-HTTPs requests
        cross_subdomain_cookie: false, // prevent cookie persistence across subdomains
        opt_out_tracking_by_default: shouldDisableCostlyVendors,
        record_sessions_percent: mixpanelReplaysPercent,
        record_heatmap_data: true,
      } as any)
    }
  }, [enableMixpanelDebug, mixpanelToken, mixpanelInitialized, cookieDomain])

  useEffect(() => {
    if (mixpanelInitialized && sessionId) {
      mixpanel.register({ session_id: sessionId })
    }
  }, [mixpanelInitialized, sessionId])

  useEffect(() => {
    if (mixpanelInitialized && connectedServiceLoadingStatus === FetchStatus.SUCCESS) {
      mixpanel.register({ [COMMON_MIXPANEL_PROPERTIES.PERSONIFY_ACTIVATION]: isPersonifyActivated })
    }
  }, [mixpanelInitialized, connectedServiceLoadingStatus, isPersonifyActivated])

  useEffect(() => {
    if (mixpanelInitialized && benefitPartner !== undefined) {
      mixpanel.register({ [COMMON_MIXPANEL_PROPERTIES.BENEFIT_PARTNER]: benefitPartner })
    }
  }, [mixpanelInitialized, benefitPartner])

  const userId = user?.id
  const username = user.username
  const customerId = user.customers?.id
  const customerName = user.customers?.name

  useEffect(() => {
    if (mixpanelInitialized && userId) {
      mixpanelTrackingOptInOrOut(username)
      mixpanel.identify(userId)
      mixpanel.people.set({
        customerId,
        customerName,
        userId,
      })
    }
  }, [customerId, customerName, mixpanelInitialized, userId, username])

  return mixpanelInitialized
}

export default useMixpanel
